import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params, Router, Event, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';

import { takeUntil, switchMap, filter, pluck } from 'rxjs/operators';
import { ReplaySubject, combineLatest, merge, of } from 'rxjs';

import { AppState, Actions, Selectors } from 'store/root-reducer';

import { Crp } from 'modules/shared/models/crp.models';
import { CrpResponse } from 'store/models/crp.models';
import { Customer } from 'modules/shared/models/customer.models';
import { IndividualRecord, CorporateRecord } from 'modules/shared/models/record.models';

import { CustomerService } from 'modules/core/services/customer.service';

@Component({
  selector: 'artemis-customer-particulars',
  templateUrl: './customer-particulars.component.html',
  styleUrls: ['./customer-particulars.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomerParticularsComponent implements OnInit, OnDestroy {
  private unsubscribe$: ReplaySubject<any> = new ReplaySubject<any>(1);

  customer: Customer;
  crp: Crp;
  record: IndividualRecord | CorporateRecord;
  dataAvailableFormFields: any;


  constructor(private activatedRoute: ActivatedRoute,
    private store: Store<AppState>,
    private router: Router,
    private customerService: CustomerService,
    private cdRef: ChangeDetectorRef) { }

  ngOnInit() {
    this.activatedRoute
      .params
      .pipe(
        takeUntil(this.unsubscribe$)
      ).subscribe((params: Params) => {
        this.store.dispatch(new Actions.customer.GetCustomer({ customerId: params['id'] }));
      });


    let crpId = null;
    if (this.activatedRoute.firstChild) {
      crpId = this.activatedRoute.firstChild.snapshot.params['crpId'];
    }
    this.initCustomerParticulars(crpId);
    this.checkCrpChanges();

    this.customerService.getAppConfig()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(item => {
        this.dataAvailableFormFields = item;
    });
  }

  ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
    this.store.dispatch(new Actions.record.ResetRecord());
    this.store.dispatch(new Actions.customer.ResetCustomerOverview());
  }

  private checkCrpChanges(): void {
    this.router.events.pipe(
      takeUntil(this.unsubscribe$),
      filter((event: Event) => event instanceof NavigationEnd),
      switchMap(() => {
        return (this.activatedRoute.firstChild && this.activatedRoute.firstChild.params)
          || of({});
      }),
      pluck('crpId')
    ).subscribe((crpId: string) => {
      if (crpId) {
        this.initCustomerParticulars(crpId);
      } else {
        this.initCustomerParticulars();
      }
    });
  }

  private initCustomerParticulars(crpId?: string): void {
    combineLatest([
      merge(
        this.store.select(Selectors.record.getIndividualRecord),
        this.store.select(Selectors.record.getCorporateRecord)
      ),
      this.store.select(Selectors.customer.getCustomer),
      this.store.select(Selectors.customer.getCrps)
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([record, customer, crps]: [IndividualRecord | CorporateRecord, Customer, CrpResponse]) => {
        if (record && customer) {
          this.record = record;
          this.customer = customer;
          const handshakeReport = this.record['handshakesReports'];
          const handShakesReportExist = handshakeReport !== undefined;
          if (handShakesReportExist && handshakeReport.length > 0) {
            const latestHandshakeReport = handshakeReport.length - 1;
            if (this.record.addresses.length === 0 && handshakeReport[latestHandshakeReport].hasOwnProperty('currentAddress')) {
              const handshakeAddress = this.record['handshakesReports'][latestHandshakeReport]['currentAddress'];
              this.record.addresses = [handshakeAddress];
            }
          }
        }

        if (crps.results && crps.results.length > 0) {
          if (crpId) {
            this.crp = crps.results.find((crp: Crp) => crp.id.toString() === crpId);
          } else {
            this.crp = crps.results.find((crp: Crp) => crp.primary === true);
          }
        }

        // Change detection on destroyed view
        if (customer) {
          this.cdRef.detectChanges();
        }
      });
  }
}
